import _ from 'lodash';

import { loadPdfWithBaseSizes } from 'utils/documents';
import { logger } from 'utils/logger';
import { getPdfJsDocument } from 'utils/pdf';

import { TemplateChangeKind } from '../../FormChangeHistory.constants';

import { MIN_MARK_HEIGHT_PT, PAGE_ORIGIN } from './PagedTemplatePreview.constants';

const addBox = (boxesById, nodeId, box) => {
  if (nodeId) {
    boxesById.set(nodeId, [...(boxesById.get(nodeId) ?? []), box]);
  }
};

// A line's runs sit at x from the line's start, shifted by its alignment; widgets and pictures are parts.
const collectLineBoxes = ({ boxesById, block, origin, pageIndex }) =>
  _.forEach(block.lines, line =>
    _.forEach(line.runs, run =>
      addBox(boxesById, run.widgetNodeId ?? run.imageNodeId, {
        pageIndex,
        x: origin.x + (line.alignOffset ?? 0) + run.x,
        y: origin.y + line.y,
        width: run.width,
        height: line.height,
      }),
    ),
  );

// Rows and cells sit relative to their table, a cell's blocks relative to the cell.
const collectBlockBoxes = ({ boxesById, blocks, origin, pageIndex }) =>
  _.forEach(blocks, block => {
    const blockOrigin = { x: origin.x + block.x, y: origin.y + block.y };

    addBox(boxesById, block.nodeId, {
      pageIndex,
      ...blockOrigin,
      width: block.width,
      height: block.height,
    });
    collectLineBoxes({ boxesById, block, origin: blockOrigin, pageIndex });

    _.forEach(block.table?.rows, row => {
      addBox(boxesById, row.nodeId, {
        pageIndex,
        x: blockOrigin.x,
        y: blockOrigin.y + row.y,
        width: block.width,
        height: row.height,
      });

      _.forEach(row.cells, cell => {
        const cellOrigin = { x: blockOrigin.x + cell.x, y: blockOrigin.y + cell.y };

        addBox(boxesById, cell.nodeId, {
          pageIndex,
          ...cellOrigin,
          width: cell.width,
          height: cell.height,
        });
        collectBlockBoxes({ boxesById, blocks: cell.contentBlocks, origin: cellOrigin, pageIndex });
      });
    });
  });

/**
 * Where every node of a laid-out Prism document is drawn, in points from its page's top-left: the
 * header, body and footer blocks, their lines' widgets and pictures, table rows and cells, and the
 * floating images. A node split across pages has a box on each.
 */
export const getLayoutBoxes = layout => {
  const boxesById = new Map();

  layout.pages.forEach((page, pageIndex) => {
    [page.header?.blocks, page.blocks, page.footer?.blocks].forEach(blocks =>
      collectBlockBoxes({ boxesById, blocks, origin: PAGE_ORIGIN, pageIndex }),
    );
    _.forEach(page.floatingImages, image =>
      addBox(boxesById, image.nodeId, {
        pageIndex,
        x: image.x,
        y: image.y,
        width: image.width,
        height: image.height,
      }),
    );
  });

  return { pageSizes: layout.pages.map(({ width, height }) => ({ width, height })), boxesById };
};

/**
 * Lays a stored Prism document out as PrismRender does, on the editor's own engine and the margins
 * the server resolves (the document's, else its preset, else Normal), to find where each part is.
 * Sample values are filled server-side only, so a line holding a field can wrap a little differently.
 */
const layoutTemplateJson = async json => {
  const {
    DocumentModel,
    extractPageSettingsFromJson,
    fromJSON,
    layoutDocument,
    MARGIN_PRESETS,
    MarginPreset,
  } = await import('@lender/prism-editor');
  const pageSettings = extractPageSettingsFromJson(json);

  await document.fonts?.ready;

  return getLayoutBoxes(
    layoutDocument(new DocumentModel(fromJSON(json)), {
      margins:
        pageSettings.margins ??
        MARGIN_PRESETS[pageSettings.marginPreset] ??
        MARGIN_PRESETS[MarginPreset.Normal],
    }),
  );
};

// Without a layout the pages still show; only the marks are missing.
const readLayout = (requests, auditId, side) =>
  requests
    .getJson(auditId, side)
    .then(json => (json ? layoutTemplateJson(json) : null))
    .catch(error => {
      logger.error(error);

      return null;
    });

/**
 * One side's pages, its template filled with sample data whatever its kind; a Prism side also brings
 * its layout to place the marks. The loading task owns the pdf.js worker; whoever keeps the answer
 * destroys it.
 */
export const loadPreviewDocument = async ({ requests, auditId, side, templateKind }) => {
  const [blob, layout] = await Promise.all([
    requests.getPreviewPdf(auditId, side),
    templateKind === TemplateChangeKind.PRISM
      ? readLayout(requests, auditId, side)
      : Promise.resolve(null),
  ]);
  const loadingTask = await getPdfJsDocument({ data: new Uint8Array(await blob.arrayBuffer()) });
  const { pdf, pageBaseSizes } = await loadPdfWithBaseSizes(loadingTask);

  return { loadingTask, pdf, pageSizes: pageBaseSizes, layout };
};

export const releasePreviewDocument = preview => preview?.loadingTask.destroy();

// One box around all of a part's boxes on a page, as fractions of the page.
const toPlacement = (boxes, pageSize) => {
  const left = _.min(_.map(boxes, 'x'));
  const top = _.min(_.map(boxes, 'y'));
  const right = _.max(boxes.map(box => box.x + box.width));
  const bottom = _.max(boxes.map(box => box.y + box.height));

  return {
    left: left / pageSize.width,
    top: top / pageSize.height,
    width: (right - left) / pageSize.width,
    height: Math.max(bottom - top, MIN_MARK_HEIGHT_PT) / pageSize.height,
  };
};

/** Each mark's boxes, per pane and page, from the panes' layouts (older first); none where not found. */
export const getMarkPlacements = (marks, layouts) =>
  marks.map(mark => ({
    ...mark,
    placements: mark.panes.flatMap(pane => {
      const layout = layouts[pane];
      const boxesByPage = _.groupBy(layout?.boxesById.get(mark.id) ?? [], 'pageIndex');

      return _.map(boxesByPage, (boxes, pageIndex) => ({
        pane,
        pageNumber: Number(pageIndex) + 1,
        ...toPlacement(boxes, layout.pageSizes[pageIndex]),
      }));
    }),
  }));

const isOnPage = pageNumber => placement => placement.pageNumber === pageNumber;

export const getPaneMarks = (placedMarks, pane, pageNumber) =>
  placedMarks.flatMap(({ id, number, placements }) =>
    placements
      .filter(placement => placement.pane === pane && placement.pageNumber === pageNumber)
      .map(placement => ({ id, number, ...placement })),
  );

export const getMarksOnPage = (placedMarks, pageNumber) =>
  placedMarks.filter(mark => mark.placements.some(isOnPage(pageNumber)));

/** How many marks each page carries, on either side: `{ [pageNumber]: count }`. */
export const getMarkCountsByPage = placedMarks =>
  _.countBy(placedMarks.flatMap(mark => _.uniq(_.map(mark.placements, 'pageNumber'))));

export const getOtherMarkedPages = (markCounts, pageNumber) =>
  _.sortBy(
    _.map(markCounts, (count, page) => ({ pageNumber: Number(page), count })).filter(
      page => page.pageNumber !== pageNumber,
    ),
    'pageNumber',
  );

export const getUnplacedMarks = placedMarks =>
  placedMarks.filter(mark => _.isEmpty(mark.placements));
